import React, { useState, useEffect, useRef, useCallback } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../app/store'
import { TopBar, SearchBarSection } from '@/components/home'
import StatusFilterButtons, { StatusFilter } from '../components/orders/StatusFilterButtons'
import DateFilterButton from '../components/orders/DateFilterButton'
import DropdownFilters from '../components/orders/DropdownFilters'
import OrdersGrid from '../components/orders/OrdersGrid'
import OrdersGridSkeleton from '../components/orders/OrdersGridSkeleton'
import { Order } from '@/types'
import { fetchCashierOrders, CashierOrdersParams } from '../services/ordersService'
import { fetchBranches, Branch } from '../services/branchesService'
import LoadingSpinner from '../components/LoadingSpinner'

interface OrdersPageProps {
  className?: string
}

const formatDateParam = (date?: Date | string | null): string | undefined => {
  if (!date) return undefined
  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd')
  }
  if (typeof date === 'string') {
    return date.split('T')[0]
  }
  return undefined
}

const OrdersPage: React.FC<OrdersPageProps> = ({ className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Redux state
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)

  // Local state
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('all')
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  
  // Filter state - all filters default to "all" (undefined)
  const [selectedTypeId, setSelectedTypeId] = useState<number | string | undefined>(undefined)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>(undefined)
  const [selectedPlatform, setSelectedPlatform] = useState<string | undefined>(undefined)
  const [selectedBranchId, setSelectedBranchId] = useState<number | string | undefined>(undefined)
  const [branches, setBranches] = useState<Branch[]>([])
  const [dateFilter, setDateFilter] = useState<{
    startDate: Date | string | null
    endDate: Date | string | null
  }>({
    startDate: null,
    endDate: null,
  })
  
  // Scroll container ref
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const isRTL = settings.language === 'ar'

  // Function to load branches
  const loadBranches = useCallback(async () => {
    try {
      const branchesData = await fetchBranches()
      setBranches(branchesData)
    } catch (err) {
      // Don't show error to user, just log it
    }
  }, [])

  // Fetch branches on mount
  useEffect(() => {
    loadBranches()
  }, [loadBranches])

  // Refresh branches and clear all filters when language changes
  useEffect(() => {
    const handleLanguageChange = async () => {
      // Clear all filters when language changes to ensure fresh data with new language
      // This will trigger the existing useEffect that reloads orders
      setSelectedBranchId(undefined)
      setSelectedTypeId(undefined)
      setSelectedPaymentMethod(undefined)
      setSelectedPlatform(undefined)
      // Reset to first page
      setCurrentPage(1)
      setOrders([])
      // Reload branches with new language
      await loadBranches()
    }

    // Listen for language change event
    window.addEventListener('languageChanged', handleLanguageChange as (event: Event) => void)
    
    // Also check if language changed via settings
    const previousLanguage = localStorage.getItem('previous_language')
    if (previousLanguage && previousLanguage !== settings.language) {
      handleLanguageChange()
    }

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as (event: Event) => void)
    }
  }, [settings.language, loadBranches])

  // Map status filter to statusId
  const getStatusId = (status: StatusFilter): number | undefined => {
    switch (status) {
      case 'preparing':
      case 'ready':
        return 1 // نشط (يمكن أن يكون 1, 2, أو 3)
      case 'completed':
        return 4 // تم
      case 'pending':
        return 6 // معلق (يمكن أن يكون 6 أو 9)
      case 'cancelled':
        return undefined
      default:
        return undefined
    }
  }

  const parseOrderTypeValue = (value: string): number | undefined => {
    switch (value) {
      case '1':
        return 1
      case '2':
        return 2
      case '3':
        return 3
      case '4':
        return 4
      default:
        return undefined
    }
  }

  // Fetch orders function
  const loadOrders = useCallback(async (page: number = 1, append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
      setError(null)
      setOrders([]) // Clear orders when loading new page
    }

    try {
      const params: CashierOrdersParams = {
        typeId: selectedTypeId,
        branchId: selectedBranchId,
        paymentMethod: selectedPaymentMethod,
        platform: selectedPlatform,
        page,
      }
      
      // Add status filter - only if not 'all'
      if (selectedStatus !== 'all') {
        const statusId = getStatusId(selectedStatus)
        if (statusId !== undefined) {
          params.statusId = statusId
        }
      }

      // Add date filters
      const startDateParam = formatDateParam(dateFilter.startDate)
      if (startDateParam) {
        params.dateFrom = startDateParam
      }
      
      const endDateParam = formatDateParam(dateFilter.endDate)
      if (endDateParam) {
        params.dateTo = endDateParam
      }

      const trimmedSearch = searchQuery.trim()
      if (trimmedSearch) {
        params.search = trimmedSearch
      }

      const response = await fetchCashierOrders(params, append)
      
      if (append) {
        setOrders(prev => {
          const newOrders = [...prev, ...response.orders]
          return newOrders
        })
      } else {
        setOrders(response.orders)
      }
      
      setCurrentPage(response.meta.currentPage)
      const hasMorePages = response.meta.currentPage < response.meta.lastPage
      setHasMore(hasMorePages)
    } catch (err: any) {
      // Check if it's a 401 error
      if (err?.response?.status === 401 || err?.status === 401) {
        // Don't set error message here - the global handler will show a toast
        // and redirect to login
        setError(null)
      } else {
        // Convert technical error messages to user-friendly messages
        let userFriendlyMessage = t('common.error') || 'حدث خطأ في تحميل الطلبات'
        
        const status = err?.response?.status || err?.status
        const errorMessage = err?.message || ''
        
        // Handle specific error status codes
        if (status === 500) {
          userFriendlyMessage = t('error.server_error') || 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى'
        } else if (status === 403) {
          userFriendlyMessage = t('error.unauthorized_error') || 'غير مصرح لك بالوصول إلى هذه البيانات'
        } else if (status === 404) {
          userFriendlyMessage = t('error.not_found') || 'البيانات المطلوبة غير موجودة'
        } else if (status === 422) {
          userFriendlyMessage = t('error.validation_error') || 'البيانات المرسلة غير صحيحة'
        } else if (errorMessage.includes('Network Error') || errorMessage.includes('network')) {
          userFriendlyMessage = t('error.network_error') || 'خطأ في الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت'
        } else if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
          userFriendlyMessage = t('error.timeout_error') || 'انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى'
        } else if (errorMessage.includes('Request failed with status code')) {
          // Generic status code error - show user-friendly message
          userFriendlyMessage = t('error.server_error') || 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى'
        } else if (err?.response?.data?.message) {
          // Use server-provided message if available
          userFriendlyMessage = err.response.data.message
        } else if (errorMessage && !errorMessage.includes('status code')) {
          // Only use error message if it's not a technical status code message
          userFriendlyMessage = errorMessage
        }
        
        setError(userFriendlyMessage)
      }
      setHasMore(false) // Stop trying if there's an error
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [selectedStatus, selectedTypeId, selectedPaymentMethod, selectedPlatform, selectedBranchId, dateFilter, searchQuery, t])

  // Function to load more orders (for scroll loading)
  const fetchMoreData = useCallback(() => {
    if (!isLoadingMore && hasMore && !isLoading) {
      const nextPage = currentPage + 1
      loadOrders(nextPage, true)
    }
  }, [currentPage, hasMore, isLoadingMore, isLoading, loadOrders])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore) return

    const loadMoreElement = loadMoreRef.current
    const scrollContainer = scrollContainerRef.current
    
    if (!loadMoreElement || !scrollContainer) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (
          entry.isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          fetchMoreData()
        }
      },
      {
        root: scrollContainer,
        rootMargin: '0px',
        threshold: 0,
      }
    )

    // Small delay to ensure element is rendered
    const timeoutId = setTimeout(() => {
      if (loadMoreElement) {
        observer.observe(loadMoreElement)
      }
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [hasMore, isLoading, isLoadingMore, fetchMoreData, orders.length])

  // Initial load and when filters change
  useEffect(() => {
    setCurrentPage(1)
    setHasMore(true)
    loadOrders(1, false)
  }, [loadOrders, settings.language])

  // Orders are already filtered remotely when a search query is provided
  const visibleOrders = orders


  // Filter options for dropdowns
  const filterOptions = [
    {
      type: 'branch' as const,
      label: t('orders.filter.branch'),
      options: [
        { value: 'all', label: t('orders.filter.all_statuses') },
        ...branches.map((branch) => ({
          value: branch.id.toString(),
          label: branch.name,
        })),
      ],
      selectedValue: selectedBranchId?.toString() || 'all',
      onSelect: (value: string) => {
        setSelectedBranchId(value === 'all' ? undefined : parseInt(value))
      },
    },
    {
      type: 'order_type' as const,
      label: t('orders.filter.order_type'),
      options: [
        { value: 'all', label: t('orders.filter.all_statuses') },
        { value: '1', label: t('orders.type.delivery') },
        { value: '2', label: t('orders.type.takeaway') },
        { value: '3', label: t('orders.type.dine_in') },
        { value: '4', label: t('orders.type.car_pickup') },

      ],
      selectedValue: selectedTypeId?.toString() || 'all',
      onSelect: (value: string) => {
        setSelectedTypeId(value === 'all' ? undefined : parseOrderTypeValue(value))
      },
    },
 
    {
      type: 'payment_method' as const,
      label: t('orders.filter.payment_method'),
      options: [
        { value: 'all', label: t('orders.filter.all_statuses') },
        { value: 'cash', label: t('payment.cash') },
          { value: 'online', label: t('payment.online') },
          { value: 'partial', label: t('payment.partial') },
          { value: 'network', label: t('payment.network') },
          { value: 'wallet', label: t('payment.wallet') },



      ],
      selectedValue: selectedPaymentMethod || 'all',
      onSelect: (value: string) => {
        setSelectedPaymentMethod(value === 'all' ? undefined : value)
      },
    },
  
    {
      type: 'platforms' as const,
      label: t('orders.filter.platforms'),
      options: [
        { value: 'all', label: t('orders.filter.all_statuses') },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
        { value: 'web', label: 'Web' },
        { value: 'pos', label: 'POS' },
        { value: 'cashier', label: 'Cashier' },
        { value: 'qr', label: 'QR' },
        { value: 'customer_service', label: 'Customer Service' },
      ],
      selectedValue: selectedPlatform || 'all',
      onSelect: (value: string) => {
        setSelectedPlatform(value === 'all' ? undefined : value)
      },
    },
  ]

  const handleOrderClick = (order: Order) => {
    // Navigate to order details page
    navigate(`/orders/${order.id}`)
  }

  const handleDateChange = (value: {
    startDate: Date | string | null
    endDate: Date | string | null
  }) => {
    setDateFilter(value)
  }

  const handleDateClear = () => {
    setDateFilter({
      startDate: null,
      endDate: null,
    })
  }

  return (
    <div className={`w-full h-screen bg-neutral-100 flex flex-col overflow-hidden ${className}`}>
        <div className={`${isRTL ? 'pl-6 pt-2' : 'pr-6 pt-5'}`}>
            {/* Top Bar */}
          <TopBar userName={user?.name} />
          </div>
      <div className="self-stretch flex-1 px-6 pt-4 relative flex flex-col gap-2 min-h-0">
   

        {/* Filters Section */}
        <div className="self-stretch flex flex-col gap-4">
          {/* Search and Status Filters */}
          <div className={` flex flex-row justify-between items-center`}>
         
            <StatusFilterButtons
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
            />
               <div className="  gap-3">
              <SearchBarSection
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                placeholder={t('common.search')}
              />
            </div>
          </div>

          {/* Date and Dropdown Filters */}
          <div className={`flex flex-row  items-center justify-between`}>
          <DropdownFilters filters={filterOptions}  />

            <DateFilterButton 
              value={dateFilter}
              onChange={handleDateChange}
              onClear={handleDateClear}
              isRTL={isRTL}
            />
          </div>
        </div>

        {/* Orders Grid - Scrollable Container with manual scroll loading */}
        <div 
          ref={scrollContainerRef} 
          className="self-stretch flex-1 overflow-y-auto min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style]:none [scrollbar-width]:none"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            position: 'relative',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            flex: '1 1 0%'
          }}
        >
          {isLoading ? (
            <div className="flex flex-col w-full">
              <OrdersGridSkeleton count={6} />
            </div>
          ) : error ? (
            <div className="flex flex-col justify-center items-center h-full gap-4 px-4">
              <div className="text-center max-w-md">
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <p className={`text-red-600 text-lg font-semibold mb-2 font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('error.something_went_wrong') || 'حدث خطأ'}
                </p>
                <p className={`text-gray-700 text-base font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'text-right' : 'text-left'}`}>
                  {error}
                </p>
                <button
                  onClick={() => {
                    setError(null)
                    loadOrders(1, false)
                  }}
                  className={`mt-4 px-6 py-2 bg-flutter-button text-white rounded-lg font-medium hover:bg-flutter-button/90 transition-colors font-['IBM_Plex_Sans_Arabic'] ${isRTL ? 'ml-auto' : 'mr-auto'}`}
                >
                  {t('common.retry') || 'إعادة المحاولة'}
                </button>
              </div>
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-text-secondary">{t('orders.no_orders')}</p>
            </div>
          ) : (
            <>
              <OrdersGrid
                orders={visibleOrders}
                onOrderClick={handleOrderClick}
                isRTL={isRTL}
                showLoadingIndicator={isLoadingMore && hasMore}
              />
              {hasMore && <div ref={loadMoreRef} style={{ height: '20px', width: '100%' }} />}
              {!hasMore && visibleOrders.length > 0 && (
                <div className="flex justify-center items-center py-6 mt-4">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                    <p className="text-xs text-gray-500 font-['IBM_Plex_Sans_Arabic'] font-medium">
                      {t('orders.no_more_orders', 'لا توجد المزيد من الطلبات')}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  )
}

export default OrdersPage
